"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AIAssistSuggestionHandler = void 0;
const assistant_component_types_1 = require("../types/assistant.component.types");
const signal_manager_helper_1 = require("./signal-manager.helper");
class AIAssistSuggestionHandler {
    /**
     * Transform entry actions into suggestions format
     * @param entry Assistant entry with actions
     * @returns Array of suggestion data
     */
    static transformActionsToSuggestions(entry) {
        if (!entry.actions || entry.actions.length === 0) {
            return [];
        }
        return entry.actions.map((action, index) => {
            let text = action.label;
            let description = action.label;
            // Handle text insertion actions
            if (action.context === assistant_component_types_1.AssistantActionContext.TEXT_INSERTION && 'text' in action.params) {
                text = action.params.text;
                description = action.params.text;
            }
            // Handle KDB attachment actions
            else if (action.context === assistant_component_types_1.AssistantActionContext.KDB_ATTACHMENT && 'title' in action.params) {
                description = action.params.description || action.params.title;
            }
            return {
                id: `suggestion-${index}`,
                title: action.label,
                description: description,
                text: text,
                context: action.context,
                selected: false
            };
        });
    }
    /**
     * Handle suggestion application
     * @param suggestion Applied suggestion
     * @param text Suggestion text
     * @param entry Original entry with actions
     * @param context Suggestion context
     */
    static handleSuggestionApplied(suggestion, text, entry, context) {
        // Handle KDB attachment suggestions
        if (suggestion.context === assistant_component_types_1.AssistantActionContext.KDB_ATTACHMENT) {
            this.handleKDBAttachment(suggestion, entry);
        }
        // Handle text insertion suggestions
        else if (suggestion.context === assistant_component_types_1.AssistantActionContext.TEXT_INSERTION) {
            this.handleTextInsertion(text, context);
        }
    }
    /**
     * Handle KDB attachment suggestion
     * @param suggestion KDB attachment suggestion
     * @param entry Original entry
     */
    static handleKDBAttachment(suggestion, entry) {
        var _a;
        // Find the original action to get the KDB ID
        const originalAction = (_a = entry === null || entry === void 0 ? void 0 : entry.actions) === null || _a === void 0 ? void 0 : _a.find(action => action.label === suggestion.title && action.context === assistant_component_types_1.AssistantActionContext.KDB_ATTACHMENT);
        if (originalAction && 'kdbId' in originalAction.params) {
            // Send signal to knowledge picker to attach the file
            const externalSignal = signal_manager_helper_1.AIAssistSignalManager.getExternalSignal();
            if (externalSignal) {
                externalSignal.setValue({
                    command: assistant_component_types_1.AssistantSignalCommand.ATTACH_FILE,
                    payload: { kdbId: originalAction.params.kdbId }
                });
            }
        }
    }
    /**
     * Handle text insertion suggestion
     * @param text Text to insert
     * @param context Suggestion context
     */
    static handleTextInsertion(text, context) {
        if (context.chatInputSource) {
            context.chatInputSource.value = text;
            context.chatInputSource.focus();
            // Trigger input event to update any bindings
            context.chatInputSource.dispatchEvent(new Event('input', { bubbles: true }));
            // Emit webchat:text-insert event (no auto-submit)
            context.emitEvent('webchat:text-insert', { text: text, autoSubmit: false });
        }
    }
    /**
     * Handle multiple suggestions applied
     * @param text Combined text
     * @param isMultiple Whether multiple suggestions were applied
     * @param context Suggestion context
     */
    static handleMultipleSuggestionsApplied(text, isMultiple, context) {
        console.log({ text, isMultiple, context });
        if (context.chatInputSource) {
            context.chatInputSource.value = text;
            context.chatInputSource.focus();
            // Trigger input event to update any bindings but don't submit
            context.chatInputSource.dispatchEvent(new Event('input', { bubbles: true }));
            // Emit a special event for multiple suggestions (no auto-submit)
            context.emitEvent('webchat:text-insert', { text: text, autoSubmit: false });
            console.log('input-that');
        }
    }
    /**
     * Check if entry has suggestions/actions
     * @param entry Assistant entry
     * @returns True if entry has actions
     */
    static hasSuggestions(entry) {
        return !!((entry === null || entry === void 0 ? void 0 : entry.actions) && entry.actions.length > 0);
    }
    /**
     * Get suggestion count for an entry
     * @param entry Assistant entry
     * @returns Number of suggestions
     */
    static getSuggestionCount(entry) {
        var _a;
        return ((_a = entry === null || entry === void 0 ? void 0 : entry.actions) === null || _a === void 0 ? void 0 : _a.length) || 0;
    }
    /**
     * Validate suggestion data
     * @param suggestion Suggestion to validate
     * @returns True if suggestion is valid
     */
    static isValidSuggestion(suggestion) {
        return suggestion &&
            typeof suggestion.id === 'string' &&
            typeof suggestion.title === 'string' &&
            typeof suggestion.context === 'string' &&
            Object.values(assistant_component_types_1.AssistantActionContext).includes(suggestion.context);
    }
    /**
     * Filter suggestions by context type
     * @param suggestions Array of suggestions
     * @param context Context type to filter by
     * @returns Filtered suggestions
     */
    static filterSuggestionsByContext(suggestions, context) {
        return suggestions.filter(suggestion => suggestion.context === context);
    }
    /**
     * Get text insertion suggestions only
     * @param suggestions Array of suggestions
     * @returns Text insertion suggestions
     */
    static getTextInsertionSuggestions(suggestions) {
        return this.filterSuggestionsByContext(suggestions, assistant_component_types_1.AssistantActionContext.TEXT_INSERTION);
    }
    /**
     * Get KDB attachment suggestions only
     * @param suggestions Array of suggestions
     * @returns KDB attachment suggestions
     */
    static getKDBAttachmentSuggestions(suggestions) {
        return this.filterSuggestionsByContext(suggestions, assistant_component_types_1.AssistantActionContext.KDB_ATTACHMENT);
    }
    /**
     * Create suggestion state object for component
     * @param entry Current entry
     * @param showSuggestions Whether to show suggestions
     * @returns Suggestion state
     */
    static createSuggestionState(entry, showSuggestions) {
        const suggestions = showSuggestions && entry ? this.transformActionsToSuggestions(entry) : [];
        return {
            suggestions,
            showingFullwidth: suggestions.length > 0,
            hasSuggestions: this.hasSuggestions(entry)
        };
    }
    /**
     * Reset suggestion state
     * @returns Empty suggestion state
     */
    static resetSuggestionState() {
        return {
            suggestions: [],
            showingFullwidth: false,
            hasSuggestions: false
        };
    }
}
exports.AIAssistSuggestionHandler = AIAssistSuggestionHandler;
